import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { TFunction } from 'i18next';

import type { CreateAutoCampaignPresetRequest } from 'api/autoCampaignPresets';

import type { CampaignType, FormValues } from './types';

dayjs.extend(utc);

type T = TFunction<'autoCampaignSettings'>;

export const DESCRIPTION_MAX_LENGTH = 250;
export const DISCOUNT_KEY_MAX_LENGTH = 128;
export const NAME_MAX_LENGTH = 128;
export const TRIGGER_DESCRIPTION_MAX_LENGTH = 256;
export const X_NAME_MAX_LENGTH = 128;

export const EMPTY_DRAFT: FormValues = {
  campaignType: 'evergreen',
  description: '',
  discountKey: '',
  endDate: null,
  name: '',
  startDate: null,
  triggerDescription: '',
  xName: '',
};

export function isSeasonal(campaignType: CampaignType): boolean {
  return campaignType === 'seasonal';
}

function validateRequired(value: string, maxLength: number, t: T, field: string): string | null {
  if (!value.trim()) return t('errors.required', { field });
  if (value.length > maxLength) return t('errors.tooLong', { field, maxLength });
  return null;
}

export function validateName(value: string, t: T): string | null {
  return validateRequired(value, NAME_MAX_LENGTH, t, t('CreateCampaignForm.nameLabel'));
}

export function validateXName(value: string, t: T): string | null {
  return validateRequired(value, X_NAME_MAX_LENGTH, t, t('CreateCampaignForm.xNameLabel'));
}

export function validateDescription(value: string, t: T): string | null {
  return validateRequired(
    value,
    DESCRIPTION_MAX_LENGTH,
    t,
    t('CreateCampaignForm.descriptionLabel'),
  );
}

export function validateTriggerDescription(value: string, t: T): string | null {
  return validateRequired(
    value,
    TRIGGER_DESCRIPTION_MAX_LENGTH,
    t,
    t('CreateCampaignForm.triggerDescriptionLabel'),
  );
}

export function validateDiscountKey(value: string, t: T): string | null {
  if (value.length > DISCOUNT_KEY_MAX_LENGTH) {
    return t('errors.tooLong', {
      field: t('CreateCampaignForm.discountKeyLabel'),
      maxLength: DISCOUNT_KEY_MAX_LENGTH,
    });
  }
  return null;
}

export function validateStartDate(value: string | null, values: FormValues, t: T): string | null {
  if (!isSeasonal(values.campaignType)) return null;
  if (!value) return t('errors.required', { field: t('CreateCampaignForm.startDateLabel') });
  return null;
}

export function validateEndDate(value: string | null, values: FormValues, t: T): string | null {
  if (!isSeasonal(values.campaignType)) return null;
  if (!value) return t('errors.required', { field: t('CreateCampaignForm.endDateLabel') });
  if (values.startDate && dayjs(value).isBefore(dayjs(values.startDate), 'day')) {
    return t('errors.endBeforeStart');
  }
  return null;
}

/**
 * MIS reads a null start/end pair as evergreen, so a seasonal-only field must not leak through.
 * Dates go out in UTC so the wall clock stays midnight: MIS stores these columns naive and
 * re-labels that wall clock into each restaurant's own timezone, so an admin's local midnight
 * converted to UTC would start the campaign at that offset (4 AM, say) for every restaurant.
 */
export function buildCreatePresetPayload(values: FormValues): CreateAutoCampaignPresetRequest {
  const seasonal = isSeasonal(values.campaignType);
  const discountKey = values.discountKey.trim();

  return {
    description: values.description.trim(),
    discount_key: discountKey || null,
    end_date:
      seasonal && values.endDate ? dayjs.utc(values.endDate).endOf('day').toISOString() : null,
    name: values.name.trim(),
    start_date:
      seasonal && values.startDate
        ? dayjs.utc(values.startDate).startOf('day').toISOString()
        : null,
    trigger_description: values.triggerDescription.trim(),
    x_name: values.xName.trim(),
  };
}

export function isDuplicateNameError(message: string): boolean {
  return /already exists/i.test(message);
}
